import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import api from '../lib/api';
import { Save, Upload, Bell, Trash2, Plus } from 'lucide-react';

// ---- IANA timezones (common subset) ----
const TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Honolulu',
  'America/Toronto',
  'America/Vancouver',
  'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires',
  'America/Mexico_City',
  'America/Bogota',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Moscow',
  'Europe/Istanbul',
  'Europe/Warsaw',
  'Europe/Rome',
  'Europe/Madrid',
  'Europe/Amsterdam',
  'Africa/Cairo',
  'Africa/Lagos',
  'Africa/Nairobi',
  'Africa/Johannesburg',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Dhaka',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Asia/Jakarta',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Perth',
  'Pacific/Auckland',
  'Pacific/Fiji',
];

// ---- schema ----
const settingsSchema = z.object({
  companyName: z.string().min(1, 'Company name is required'),
  workdayStart: z
    .string()
    .min(1, 'Start time is required')
    .regex(/^\d{2}:\d{2}$/, 'Must be HH:MM format'),
  workdayEnd: z
    .string()
    .min(1, 'End time is required')
    .regex(/^\d{2}:\d{2}$/, 'Must be HH:MM format'),
  timezone: z.string().min(1, 'Select a timezone'),
  timeFormat: z.enum(['12h', '24h']),
  lateStartThresholdMins: z.number().min(0, 'Must be 0 or greater'),
  breakAlertThresholdMins: z.number().min(0, 'Must be 0 or greater'),
});

function FieldError({ error }) {
  if (!error) return null;
  return <p className="mt-1 text-xs text-red-600">{error.message}</p>;
}

function SectionTitle({ children }) {
  return (
    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-4">
      {children}
    </h3>
  );
}

export default function Settings() {
  const queryClient = useQueryClient();
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState('');
  const [logoSuccess, setLogoSuccess] = useState(false);
  const [showChannelForm, setShowChannelForm] = useState(false);
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelType, setNewChannelType] = useState('DISCORD_WEBHOOK');
  const [newChannelEndpoint, setNewChannelEndpoint] = useState('');
  const [channelFormError, setChannelFormError] = useState('');
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const [selectedChannelEvents, setSelectedChannelEvents] = useState([]);
  const [newChannelUrl, setNewChannelUrl] = useState('');
  const [editingChannelId, setEditingChannelId] = useState(null);
  const [editingEndpoint, setEditingEndpoint] = useState('');
  const [emailDefaults, setEmailDefaults] = useState({});
  const [channelSubscriptions, setChannelSubscriptions] = useState({});

  const {
    data: settingsData,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const res = await api.get('/api/settings');
      return res.data?.data || res.data || {};
    },
  });

  const {
    data: channelsData = [],
    isLoading: channelsLoading,
  } = useQuery({
    queryKey: ['notification-channels'],
    queryFn: async () => {
      const res = await api.get('/api/settings/channels');
      return res.data?.data || [];
    },
  });

  const {
    data: eventsData = [],
    isLoading: eventsLoading,
  } = useQuery({
    queryKey: ['notification-events'],
    queryFn: async () => {
      const res = await api.get('/api/settings/events');
      return res.data?.data || [];
    },
  });

  const {
    data: emailDefaultsData = [],
    isLoading: emailDefaultsLoading,
  } = useQuery({
    queryKey: ['employee-email-defaults'],
    queryFn: async () => {
      const res = await api.get('/api/settings/email-defaults');
      return res.data?.data || [];
    },
  });

  const addChannelMutation = useMutation({
    mutationFn: async (channelData) => {
      const res = await api.post('/api/settings/channels', channelData);
      return res.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-channels'] });
      setShowChannelForm(false);
      setNewChannelName('');
      setNewChannelUrl('');
      setChannelFormError('');
    },
    onError: (err) => {
      setChannelFormError(err?.response?.data?.error || 'Failed to add channel');
    },
  });

  const updateChannelMutation = useMutation({
    mutationFn: async ({ id, enabled }) => {
      const res = await api.put(`/api/settings/channels/${id}`, { enabled });
      return res.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-channels'] });
    },
  });

  const deleteChannelMutation = useMutation({
    mutationFn: async (id) => {
      await api.delete(`/api/settings/channels/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-channels'] });
    },
  });

  // Fetch subscriptions for all channels
  useEffect(() => {
    const fetchAllSubscriptions = async () => {
      if (!channelsData || channelsData.length === 0) return;

      const subs = {};
      for (const channel of channelsData) {
        try {
          const res = await api.get(`/api/settings/channels/${channel.id}/subscriptions`);
          subs[channel.id] = res.data?.data?.map(s => s.event) || [];
        } catch (err) {
          console.error(`Failed to fetch subscriptions for channel ${channel.id}:`, err);
          subs[channel.id] = [];
        }
      }
      setChannelSubscriptions(subs);
    };

    fetchAllSubscriptions();
  }, [channelsData]);

  const editChannelMutation = useMutation({
    mutationFn: async ({ id, endpoint }) => {
      const res = await api.put(`/api/settings/channels/${id}`, { endpoint });
      return res.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-channels'] });
      setEditingChannelId(null);
      setEditingEndpoint('');
    },
    onError: (err) => {
      setChannelFormError(err?.response?.data?.error || 'Failed to update channel');
    },
  });

  const updateEmailDefaultMutation = useMutation({
    mutationFn: async ({ event, enabled }) => {
      const res = await api.put(`/api/settings/email-defaults/${event}`, { enabled });
      return res.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-email-defaults'] });
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      companyName: '',
      workdayStart: '09:00',
      workdayEnd: '17:00',
      timezone: 'UTC',
      timeFormat: '12h',
      lateStartThresholdMins: 50,
      breakAlertThresholdMins: 30,
    },
  });

  // Pre-populate form when data loads
  useEffect(() => {
    if (settingsData && settingsData.companyName !== undefined) {
      reset({
        companyName: settingsData.companyName || '',
        workdayStart: settingsData.workdayStart || settingsData.workStartTime || '09:00',
        workdayEnd: settingsData.workdayEnd || settingsData.workEndTime || '17:00',
        timezone: settingsData.timezone || 'UTC',
        timeFormat: settingsData.timeFormat || '12h',
        lateStartThresholdMins: settingsData.lateStartThresholdMins ?? 50,
        breakAlertThresholdMins: settingsData.breakAlertThresholdMins ?? 30,
      });
    }
  }, [settingsData, reset]);

  // Initialize email defaults from loaded data
  useEffect(() => {
    if (emailDefaultsData.length > 0) {
      const defaults = {};
      emailDefaultsData.forEach((item) => {
        defaults[item.event] = item.enabled;
      });
      setEmailDefaults(defaults);
    }
  }, [emailDefaultsData]);

  const saveMutation = useMutation({
    mutationFn: async (values) => {
      const res = await api.put('/api/settings', {
        companyName: values.companyName,
        workStartTime: values.workdayStart,
        workEndTime: values.workdayEnd,
        timezone: values.timezone,
        timeFormat: values.timeFormat,
        lateStartThresholdMins: values.lateStartThresholdMins,
        breakAlertThresholdMins: values.breakAlertThresholdMins,
      });
      return res.data;
    },
  });

  const onSubmit = async (values) => {
    try {
      await saveMutation.mutateAsync(values);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      reset(values);
    } catch {
      // error shown via saveMutation.isError
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;
    setLogoUploading(true);
    setLogoError('');
    setLogoSuccess(false);
    try {
      const formData = new FormData();
      formData.append('logo', logoFile);
      await api.post('/api/settings/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLogoSuccess(true);
      setLogoFile(null);
      setTimeout(() => setLogoSuccess(false), 3000);
    } catch (err) {
      setLogoError(err?.response?.data?.error || 'Logo upload failed.');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleAddChannel = async () => {
    setChannelFormError('');
    if (!newChannelName.trim() || !newChannelEndpoint.trim()) {
      setChannelFormError('Channel name and endpoint are required');
      return;
    }
    try {
      await addChannelMutation.mutateAsync({
        name: newChannelName.trim(),
        type: newChannelType,
        endpoint: newChannelEndpoint.trim(),
        enabled: true,
      });
      setNewChannelName('');
      setNewChannelType('DISCORD_WEBHOOK');
      setNewChannelEndpoint('');
    } catch {
      // error handled by mutation
    }
  };

  const handleSelectChannelForEvents = async (channelId) => {
    setSelectedChannelId(channelId);
    try {
      const res = await api.get(`/api/settings/channels/${channelId}/subscriptions`);
      setSelectedChannelEvents(res.data?.data?.map(s => s.event) || []);
    } catch (err) {
      console.error('Failed to fetch channel subscriptions:', err);
    }
  };

  const handleSaveChannelEvents = async () => {
    try {
      await api.post(`/api/settings/channels/${selectedChannelId}/subscriptions`, {
        events: selectedChannelEvents,
      });
      queryClient.invalidateQueries({ queryKey: ['notification-channels'] });
      setSelectedChannelId(null);
    } catch (err) {
      console.error('Failed to save channel events:', err);
    }
  };

  const handleEditChannel = (channelId, currentEndpoint) => {
    setEditingChannelId(channelId);
    setEditingEndpoint(currentEndpoint);
  };

  const handleSaveChannelEndpoint = async () => {
    if (!editingEndpoint.trim()) {
      setChannelFormError('Endpoint cannot be empty');
      return;
    }
    try {
      await editChannelMutation.mutateAsync({
        id: editingChannelId,
        endpoint: editingEndpoint.trim(),
      });
    } catch {
      // error handled by mutation
    }
  };

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <p className="text-red-600 font-medium">Failed to load settings.</p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="bg-white rounded-xl border border-gray-200 p-6 space-y-3">
              <div className="h-3 bg-gray-200 rounded animate-pulse w-1/4" />
              <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
              <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
            </div>
          ))}
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* Save success / error banners */}
          {saveSuccess && (
            <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700">
              Settings saved successfully.
            </div>
          )}
          {saveMutation.isError && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {saveMutation.error?.response?.data?.error || 'Failed to save settings.'}
            </div>
          )}

          {/* Company */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
            <SectionTitle>Company</SectionTitle>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Name
              </label>
              <input
                type="text"
                {...register('companyName')}
                className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.companyName ? 'border-red-400' : 'border-gray-300'
                }`}
                placeholder="Acme Corp"
              />
              <FieldError error={errors.companyName} />
            </div>
          </div>

          {/* Logo upload (separate panel, not part of form PUT) */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
            <SectionTitle>Company Logo</SectionTitle>
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upload Logo
                </label>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp"
                  onChange={(e) => {
                    setLogoFile(e.target.files[0] || null);
                    setLogoError('');
                    setLogoSuccess(false);
                  }}
                  className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border file:border-gray-300 file:text-xs file:font-medium file:text-gray-700 file:bg-gray-50 hover:file:bg-gray-100 file:transition-colors cursor-pointer"
                />
                <p className="mt-1 text-xs text-gray-400">
                  PNG, JPG, SVG, WebP — max 2MB
                </p>
                {logoError && <p className="mt-1 text-xs text-red-600">{logoError}</p>}
                {logoSuccess && (
                  <p className="mt-1 text-xs text-green-600">Logo uploaded successfully.</p>
                )}
              </div>
              <button
                type="button"
                onClick={handleLogoUpload}
                disabled={!logoFile || logoUploading}
                className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mt-5"
              >
                <Upload className="w-4 h-4" />
                {logoUploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>

          {/* Display settings */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
            <SectionTitle>Display</SectionTitle>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Time Format</label>
              <div className="flex gap-3">
                {[
                  { value: '12h', label: '12-hour', example: '2:30 PM' },
                  { value: '24h', label: '24-hour', example: '14:30' },
                ].map(({ value, label, example }) => (
                  <label key={value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value={value}
                      {...register('timeFormat')}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{label}</span>
                    <span className="text-xs text-gray-400">({example})</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Working hours */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
            <SectionTitle>Working Hours</SectionTitle>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Time
                </label>
                <input
                  type="time"
                  {...register('workdayStart')}
                  className={`w-full px-3 py-2 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.workdayStart ? 'border-red-400' : 'border-gray-300'
                  }`}
                />
                <FieldError error={errors.workdayStart} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Time
                </label>
                <input
                  type="time"
                  {...register('workdayEnd')}
                  className={`w-full px-3 py-2 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.workdayEnd ? 'border-red-400' : 'border-gray-300'
                  }`}
                />
                <FieldError error={errors.workdayEnd} />
              </div>
            </div>
          </div>

          {/* Timezone */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
            <SectionTitle>Timezone</SectionTitle>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                System Timezone
              </label>
              <select
                {...register('timezone')}
                className={`w-full px-3 py-2 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.timezone ? 'border-red-400' : 'border-gray-300'
                }`}
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
              <FieldError error={errors.timezone} />
              <p className="mt-1 text-xs text-gray-400">
                All timestamps in reports and time logs will display in this timezone.
              </p>
            </div>
          </div>

          {/* Notification Channels & Events */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-gray-400" />
                <SectionTitle>Notification Channels</SectionTitle>
              </div>
              <button
                type="button"
                onClick={() => setShowChannelForm(!showChannelForm)}
                disabled={addChannelMutation.isPending}
                className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <Plus className="w-3 h-3" />
                Add Channel
              </button>
            </div>

            {/* Create Channel Form */}
            {channelFormError && (
              <p className="mb-4 p-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700">
                {channelFormError}
              </p>
            )}

            {showChannelForm && (
              <div className="mb-4 p-4 border border-blue-200 rounded-lg bg-blue-50 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Channel Name
                  </label>
                  <input
                    type="text"
                    value={newChannelName}
                    onChange={(e) => setNewChannelName(e.target.value)}
                    placeholder="e.g., Admin Tracker, Daily Summary"
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    Give this channel a descriptive name
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Channel Type
                  </label>
                  <select
                    value={newChannelType}
                    onChange={(e) => setNewChannelType(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="DISCORD_WEBHOOK">Discord Webhook</option>
                    <option value="EMAIL">Email</option>
                    <option value="SLACK_WEBHOOK">Slack Webhook</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Endpoint
                  </label>
                  <input
                    type="text"
                    value={newChannelEndpoint}
                    onChange={(e) => setNewChannelEndpoint(e.target.value)}
                    placeholder={
                      newChannelType === 'DISCORD_WEBHOOK'
                        ? 'https://discord.com/api/webhooks/...'
                        : newChannelType === 'EMAIL'
                        ? 'admin@company.com'
                        : 'https://hooks.slack.com/services/...'
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    {newChannelType === 'DISCORD_WEBHOOK'
                      ? 'Get from Discord: Right-click channel → Integrations → Webhooks'
                      : newChannelType === 'EMAIL'
                      ? 'Email address to receive notifications'
                      : 'Get from Slack: Create incoming webhook'}
                  </p>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleAddChannel}
                    disabled={addChannelMutation.isPending}
                    className="flex-1 px-3 py-2 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {addChannelMutation.isPending ? 'Adding...' : 'Add Channel'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowChannelForm(false)}
                    className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Channels List */}
            {channelsLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((n) => (
                  <div key={n} className="h-12 bg-gray-100 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : channelsData.length === 0 ? (
              <p className="text-sm text-gray-400 py-4">No notification channels configured yet.</p>
            ) : (
              <div className="space-y-3">
                {channelsData.map((channel) => {
                  const channelTypeLabel = {
                    DISCORD_WEBHOOK: '🔵 Discord',
                    EMAIL: '📧 Email',
                    SLACK_WEBHOOK: '💜 Slack',
                  }[channel.type] || channel.type;

                  return (
                    <div
                      key={channel.id}
                      className={`p-4 border rounded-lg transition-colors ${
                        channel.enabled
                          ? 'border-blue-200 bg-blue-50'
                          : 'border-gray-200 bg-gray-50 opacity-60'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="text-sm font-medium text-gray-800">
                              {channel.name}
                            </h4>
                            <span className="text-xs font-medium text-gray-600 bg-gray-200 px-2 py-0.5 rounded">
                              {channelTypeLabel}
                            </span>
                            {channel.enabled ? (
                              <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded">
                                Active
                              </span>
                            ) : (
                              <span className="text-xs font-medium text-gray-600 bg-gray-300 px-2 py-0.5 rounded">
                                Disabled
                              </span>
                            )}
                          </div>
                          {editingChannelId === channel.id ? (
                            <div className="mt-2 space-y-2">
                              <input
                                type="text"
                                value={editingEndpoint}
                                onChange={(e) => setEditingEndpoint(e.target.value)}
                                placeholder={
                                  channel.type === 'DISCORD_WEBHOOK'
                                    ? 'https://discord.com/api/webhooks/...'
                                    : channel.type === 'EMAIL'
                                    ? 'admin@company.com'
                                    : 'https://hooks.slack.com/...'
                                }
                                className="w-full px-2 py-1 text-xs rounded border border-blue-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                              />
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={handleSaveChannelEndpoint}
                                  disabled={editChannelMutation.isPending}
                                  className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingChannelId(null)}
                                  className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-xs text-gray-600 truncate font-mono" title={channel.endpoint}>
                                {channel.endpoint.length > 50
                                  ? `${channel.endpoint.substring(0, 20)}...${channel.endpoint.substring(channel.endpoint.length - 20)}`
                                  : channel.endpoint}
                              </p>
                              <button
                                type="button"
                                onClick={() => handleEditChannel(channel.id, channel.endpoint)}
                                className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
                              >
                                Edit
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={channel.enabled}
                              onChange={(e) =>
                                updateChannelMutation.mutate({
                                  id: channel.id,
                                  enabled: e.target.checked,
                                })
                              }
                              className="sr-only peer"
                            />
                            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-600" />
                          </label>
                          <button
                            type="button"
                            onClick={() => deleteChannelMutation.mutate(channel.id)}
                            disabled={deleteChannelMutation.isPending}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Events for this channel */}
                      <div className="border-t pt-3">
                        <div className="flex items-center justify-between mb-2">
                          <button
                            type="button"
                            onClick={() => handleSelectChannelForEvents(channel.id)}
                            className="text-xs font-medium text-blue-600 hover:text-blue-700"
                          >
                            {selectedChannelId === channel.id ? '✓ Events Selected' : 'Choose Events'}
                          </button>
                          {channelSubscriptions[channel.id] && channelSubscriptions[channel.id].length > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                              <span className="inline-flex items-center justify-center w-4 h-4 bg-blue-600 text-white rounded-full text-[10px]">
                                {channelSubscriptions[channel.id].length}
                              </span>
                              event{channelSubscriptions[channel.id].length !== 1 ? 's' : ''} enabled
                            </span>
                          )}
                        </div>

                        {channelSubscriptions[channel.id] && channelSubscriptions[channel.id].length > 0 && selectedChannelId !== channel.id && (
                          <div className="mt-2">
                            <details className="group cursor-pointer">
                              <summary className="text-xs text-gray-600 select-none hover:text-gray-800 font-medium">
                                {channelSubscriptions[channel.id].slice(0, 2).map((eventVal) => {
                                  const eventLabel = eventsData.find((e) => e.value === eventVal)?.label || eventVal;
                                  return eventLabel;
                                }).join(', ')}
                                {channelSubscriptions[channel.id].length > 2 && (
                                  <span className="text-gray-400 group-open:hidden"> +{channelSubscriptions[channel.id].length - 2} more</span>
                                )}
                              </summary>
                              <div className="mt-2 flex flex-wrap gap-1">
                                {channelSubscriptions[channel.id].map((eventVal) => {
                                  const eventLabel = eventsData.find((e) => e.value === eventVal)?.label || eventVal;
                                  return (
                                    <span
                                      key={eventVal}
                                      className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 rounded text-[11px] border border-blue-200"
                                    >
                                      {eventLabel}
                                    </span>
                                  );
                                })}
                              </div>
                            </details>
                          </div>
                        )}

                        {selectedChannelId === channel.id && eventsData.length > 0 && (
                          <div className="mt-3 p-3 bg-white rounded border border-blue-100 space-y-2">
                            <p className="text-xs font-medium text-gray-700 mb-2">
                              Select which events trigger this channel:
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                              {eventsData.map((event) => (
                                <label
                                  key={event.value}
                                  className="flex items-start gap-2 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedChannelEvents.includes(event.value)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedChannelEvents([
                                          ...selectedChannelEvents,
                                          event.value,
                                        ]);
                                      } else {
                                        setSelectedChannelEvents(
                                          selectedChannelEvents.filter(
                                            (v) => v !== event.value
                                          )
                                        );
                                      }
                                    }}
                                    className="mt-1"
                                  />
                                  <div>
                                    <p className="text-xs font-medium text-gray-700">
                                      {event.label}
                                    </p>
                                    <p className="text-xs text-gray-400">
                                      {event.description}
                                    </p>
                                  </div>
                                </label>
                              ))}
                            </div>
                            <div className="flex gap-2 mt-3">
                              <button
                                type="button"
                                onClick={handleSaveChannelEvents}
                                className="flex-1 px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700"
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                onClick={() => setSelectedChannelId(null)}
                                className="flex-1 px-3 py-1 bg-gray-200 text-gray-700 text-xs font-medium rounded hover:bg-gray-300"
                              >
                                Done
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Employee Email Notifications - Available Events */}
            <div className="mt-6 pt-6 border-t">
              <SectionTitle>Employee Email Notifications — Available Events</SectionTitle>
              <p className="text-xs text-gray-500 mb-4">
                <strong>Enable events</strong> to make them available for employees to opt-in. Employees must enable each event to receive emails. Disabled events won't appear for employees at all.
              </p>

              {emailDefaultsLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((n) => (
                    <div key={n} className="h-10 bg-gray-100 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : eventsData.length === 0 ? (
                <p className="text-sm text-gray-400">No events available.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {eventsData.map((event) => {
                    const isEnabled = emailDefaults[event.value] ?? false;
                    return (
                      <label
                        key={event.value}
                        className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                          isEnabled
                            ? 'border-green-200 bg-green-50 hover:bg-green-100'
                            : 'border-gray-200 bg-gray-50 hover:bg-gray-100 opacity-60'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isEnabled}
                          onChange={(e) => {
                            const newState = { ...emailDefaults, [event.value]: e.target.checked };
                            setEmailDefaults(newState);
                            updateEmailDefaultMutation.mutate({
                              event: event.value,
                              enabled: e.target.checked,
                            });
                          }}
                          className="mt-1"
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-700">{event.label}</p>
                          <p className="text-xs text-gray-500">{event.description}</p>
                          {!isEnabled && (
                            <p className="text-xs text-red-600 mt-1 font-medium">
                              Disabled — employees won't see this
                            </p>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Thresholds section */}
            <div className="mt-6 pt-6 border-t">
              <SectionTitle>Alert Thresholds</SectionTitle>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Late Start Threshold (min)
                  </label>
                  <input
                    type="number"
                    {...register('lateStartThresholdMins', { valueAsNumber: true })}
                    className={`w-full px-3 py-2 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.lateStartThresholdMins ? 'border-red-400' : 'border-gray-300'
                    }`}
                    placeholder="50"
                    min="0"
                  />
                  <FieldError error={errors.lateStartThresholdMins} />
                  <p className="mt-1 text-xs text-gray-400">
                    Alert if no start by routine + this many min
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Break Alert Threshold (min)
                  </label>
                  <input
                    type="number"
                    {...register('breakAlertThresholdMins', { valueAsNumber: true })}
                    className={`w-full px-3 py-2 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.breakAlertThresholdMins ? 'border-red-400' : 'border-gray-300'
                    }`}
                    placeholder="30"
                    min="0"
                  />
                  <FieldError error={errors.breakAlertThresholdMins} />
                  <p className="mt-1 text-xs text-gray-400">
                    Alert if idle for this many min
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Save button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting || !isDirty}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <Save className="w-4 h-4" />
              {isSubmitting ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
